import Hls, {HlsConfig, LoaderCallbacks, LoaderConfiguration, LoaderContext, PlaylistLoaderConstructor} from 'hls.js';
import {EventEmitter} from 'eventemitter3';

export interface HlsPlusConfig {
    isInteractive: boolean;
    debug: boolean;
    autoPlay: boolean;
    preferNativeHls: boolean;
    sendCredentials: boolean;
    debugLogs: boolean;
    forwardedFor?: string;
    adUrl?: string;
    nowPlayingPartner?: string;
    nowPlayingComposerId?: string;
    nowPlayingPollIntervalMs?: number;
}

export enum HlsPlusEvents {
    MANIFEST_PARSED = 'MANIFEST_PARSED',
    TRACK_CHANGED = 'TRACK_CHANGED',
    TRACK_METADATA_CHANGED = 'TRACK_METADATA_CHANGED',
    SEGMENT_CHANGED = 'SEGMENT_CHANGED',
    AVAILABLE_SKIPS_CHANGED = 'AVAILABLE_SKIPS_CHANGED',
    TRACK_LOADING = 'TRACK_LOADING',
    TRACK_LOADED = 'TRACK_LOADED',
}

export type TrackMetadata = Pick<Track, 'id' | 'title' | 'album' | 'artist' | 'image' | 'duration_ms' | 'duration'>;

interface CompanionAd {
    image: string;
}

interface Track {
    id: string;
    title: string;
    album: string;
    artist: string;
    duration_ms: number;
    duration: number;
    start_ms: string;
    image: string;
    type: string;
    companion: CompanionAd | null;
    elapsed_ms?: number;
}

interface Segment {
    current: Track;
    available_skips: number;
    skip_return_ms: number;
    continuity: number;
}

interface NowPlayingTrack {
    album?: string;
    artist?: string;
    cartid?: string;
    description?: string;
    duration?: string | number;
    duration_ms?: number;
    id?: string;
    imageUrl?: string | string[];
    image?: string;
    status?: string;
    time?: string;
    title?: string;
    type?: string;
}

interface NowPlayingConfig {
    url: string;
    partner: string;
    composerId: string;
}

const NOW_PLAYING_BOUNDARY_OFFSET_MS = 500;
const NOW_PLAYING_BOUNDARY_RETRY_MS = 1_000;
const NOW_PLAYING_MIN_POLL_INTERVAL_MS = 5_000;

const withRequestHeaders = (
    headersInit: HeadersInit | undefined,
    forwardedFor?: string,
    adUrl?: string,
): Headers => {
    const headers = new Headers(headersInit);
    if (forwardedFor) {
        headers.set('x-forwarded-for', forwardedFor);
    }
    if (adUrl) {
        headers.set('ad-request-url', adUrl);
    }
    return headers;
};

const isM3u8Url = (urlLike: string): boolean => {
    try {
        return new URL(urlLike, window.location.href).pathname.endsWith('.m3u8');
    } catch {
        return urlLike.includes('.m3u8');
    }
};

const isSuperhifiDirectStreamUrl = (urlLike: string): boolean => {
    if (isM3u8Url(urlLike)) {
        return false;
    }
    try {
        const url = new URL(urlLike, window.location.href);
        return url.hostname === 'superhi.fi' ||
            url.hostname.endsWith('.superhi.fi') ||
            url.hostname === 'superhifi.radio' ||
            url.hostname.endsWith('.superhifi.radio');
    } catch {
        return false;
    }
};

const parseDurationMs = (duration?: string | number): number => {
    if (typeof duration === 'number' && Number.isFinite(duration)) {
        return duration > 1000 ? Math.round(duration) : Math.round(duration * 1000);
    }
    if (typeof duration !== 'string' || !duration) {
        return 0;
    }
    const parts = duration.split(':').map((part: string) => parseInt(part, 10));
    if (parts.some((part) => isNaN(part))) {
        return 0;
    }
    let seconds = 0;
    for (const part of parts) {
        seconds = (seconds * 60) + part;
    }
    return seconds * 1000;
};

const firstString = (value?: string | string[]): string => {
    if (Array.isArray(value)) {
        return value.find((item) => !!item) || '';
    }
    return value || '';
};

const isUuid = (value: string): boolean => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
};

const isAudioMountPath = (value: string): boolean => {
    return /^aac-\d+$/i.test(value) ||
        /^mp3-\d+$/i.test(value) ||
        /^opus-\d+$/i.test(value) ||
        /^stream$/i.test(value);
};

const extractNowPlayingTracks = (data: unknown): NowPlayingTrack[] => {
    if (Array.isArray(data)) {
        return data as NowPlayingTrack[];
    }
    if (!data || typeof data !== 'object') {
        return [];
    }
    const record = data as {[key: string]: unknown};
    for (const key of ['nowPlaying', 'items', 'tracks', 'results', 'list', 'data']) {
        if (Array.isArray(record[key])) {
            return record[key] as NowPlayingTrack[];
        }
    }
    return [];
};

const buildNowPlayingConfig = (
    streamUrlLike: string,
    partnerOverride?: string,
    composerIdOverride?: string,
): NowPlayingConfig | null => {
    try {
        const streamUrl = new URL(streamUrlLike, window.location.href);
        const hostname = streamUrl.hostname.toLowerCase();
        const isDev = hostname.includes('.dev.');
        const nowPlayingHost = isDev ? 'nowplaying.dev.superhi.fi' : 'nowplaying.superhi.fi';
        const pathParts = streamUrl.pathname
            .split('/')
            .map((part) => decodeURIComponent(part).trim())
            .filter(Boolean);
        const queryPartner = streamUrl.searchParams.get('partner') || undefined;
        const queryComposerId = streamUrl.searchParams.get('composerId') ||
            streamUrl.searchParams.get('composerid') ||
            streamUrl.searchParams.get('cid') ||
            undefined;
        const ignoredLabels = new Set(['dev', 'stream', 'streams', 'icecast', 'live', 'audio']);
        const hostLabels = hostname
            .replace(/\.superhi\.fi$/, '')
            .replace(/\.superhifi\.radio$/, '')
            .split('.')
            .filter((label) => label && !ignoredLabels.has(label));
        const meaningfulPathParts = pathParts.filter((part) => !isAudioMountPath(part));
        const streamLabel = hostLabels[0] || '';
        const streamLabelUuid = /^(.*)-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.exec(streamLabel);

        let inferredPartner = '';
        let inferredComposerId = '';
        if (streamLabelUuid) {
            inferredPartner = streamLabelUuid[1];
            inferredComposerId = streamLabelUuid[2];
        } else if (meaningfulPathParts.length >= 2) {
            inferredPartner = meaningfulPathParts[0];
            inferredComposerId = meaningfulPathParts[1];
        } else if (hostLabels.length >= 2) {
            inferredPartner = hostLabels[0];
            inferredComposerId = hostLabels[1];
        } else if (hostLabels.length === 1 && meaningfulPathParts.length === 1) {
            inferredPartner = hostLabels[0];
            inferredComposerId = meaningfulPathParts[0];
        } else if (meaningfulPathParts.length === 1 && isUuid(meaningfulPathParts[0])) {
            inferredPartner = streamLabel || meaningfulPathParts[0];
            inferredComposerId = meaningfulPathParts[0];
        }

        const partner = partnerOverride || queryPartner || inferredPartner;
        const composerId = composerIdOverride || queryComposerId || inferredComposerId;
        if (!partner || !composerId) {
            return null;
        }
        return {
            url: `https://${nowPlayingHost}/list/${encodeURIComponent(partner)}/${encodeURIComponent(composerId)}?count=5`,
            partner,
            composerId,
        };
    } catch {
        return null;
    }
};

const isServiceUrl = (urlLike: string, sourceUrlLike?: string): boolean => {
    if (!sourceUrlLike) return false;
    try {
        const normalizedUrl = new URL(urlLike, window.location.href);
        const sourceUrl = new URL(sourceUrlLike, window.location.href);
        return normalizedUrl.origin === sourceUrl.origin && normalizedUrl.pathname.includes('/hls/');
    } catch {
        return false;
    }
};

export default class HlsPlus extends EventEmitter {
    private _latestSegment: Segment | undefined;
    private _latestTrackMetadata: Partial<TrackMetadata> | undefined;
    private readonly _isInteractive: boolean;
    private readonly _autoPlay: boolean;
    private _action: 'load' | 'refresh' | 'skip' = 'refresh';
    private _continuity: number = 0;
    private _availableSkips: number = 0;
    private _mediaElem: HTMLMediaElement | undefined;
    private _sourceUrl: string | undefined;
    private _shouldBePlaying: boolean = false;
    private _playOnInit: boolean = false;
    private _hasInitializedPlayer: boolean = false;
    private _shouldResetStreamOnPlay: boolean = false;
    private _resetStreamTimoutId: ReturnType<typeof setTimeout> | undefined;
    private _isSkipping: boolean = false;
    private _isLoading: boolean = false;
    private _useNativeHls: boolean = false;
    private readonly _hlsConfig: Partial<HlsConfig>;
    private _hls: Hls | undefined;
    private _workerReg: ServiceWorkerRegistration | undefined;
    private _trackMetadataDebounceTimer: ReturnType<typeof setTimeout> | undefined;
    private _pendingTrackMetadata: TrackMetadata | undefined;
    private readonly _forwardedFor: string | undefined;
    private readonly _adUrl: string | undefined;
    private readonly _debugLogs: boolean;
    private readonly _nowPlayingPartner: string | undefined;
    private readonly _nowPlayingComposerId: string | undefined;
    private readonly _nowPlayingPollIntervalMs: number;
    private _nowPlayingPollTimeoutId: ReturnType<typeof setTimeout> | undefined;
    private _nowPlayingAbortController: AbortController | undefined;

    constructor({
                    isInteractive = true,
                    debug = false,
                    autoPlay = false,
                    preferNativeHls = false,
                    sendCredentials = true,
                    debugLogs = true,
                    forwardedFor,
                    adUrl,
                    nowPlayingPartner,
                    nowPlayingComposerId,
                    nowPlayingPollIntervalMs = 15_000,
                }: Partial<HlsPlusConfig>) {
        super();
        this._isInteractive = isInteractive;
        this._autoPlay = autoPlay;
        this._useNativeHls = preferNativeHls || !Hls.isSupported();
        this._forwardedFor = forwardedFor?.trim() || undefined;
        this._adUrl = adUrl?.trim() || undefined;
        this._debugLogs = debugLogs;
        this._nowPlayingPartner = nowPlayingPartner?.trim() || undefined;
        this._nowPlayingComposerId = nowPlayingComposerId?.trim() || undefined;
        this._nowPlayingPollIntervalMs = Math.max(nowPlayingPollIntervalMs, 5_000);
        const forwardedForHeader = this._forwardedFor;
        const adUrlHeader = this._adUrl;

        const hlsConfig: Partial<HlsConfig> = {
            autoStartLoad: true,
            enableID3MetadataCues: true,
            enableWorker: true,
            workerPath: './hls.worker.js',
            debug,
            xhrSetup: function (xhr: any, url: any) {
                if (sendCredentials) {
                    xhr.withCredentials = true; // do send cookie
                }
                if (!isServiceUrl(url, self._sourceUrl)) {
                    return;
                }
                if (forwardedForHeader) {
                    xhr.setRequestHeader('x-forwarded-for', forwardedForHeader);
                }
                if (adUrlHeader && isM3u8Url(url)) {
                    xhr.setRequestHeader('ad-request-url', adUrlHeader);
                }
            },
            fetchSetup: function (context: any, initParams: any) {
                if (sendCredentials) {
                    // Always send cookies, even for cross-origin calls.
                    initParams.credentials = 'include';
                }
                if (!isServiceUrl(context.url, self._sourceUrl)) {
                    return new Request(context.url, initParams);
                }
                initParams.headers = withRequestHeaders(
                    initParams.headers,
                    forwardedForHeader,
                    isM3u8Url(context.url) ? adUrlHeader : undefined,
                );
                return new Request(context.url, initParams);
            }
        };
        const self = this;
        if (isInteractive) {
            class CustomLoader extends Hls.DefaultConfig.loader {
                load(context: LoaderContext, config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>) {
                    const url = new URL(context.url);
                    url.searchParams.set('continuity', (self._continuity).toString());
                    url.searchParams.set('action', self._action);
                    // self.log('PLOADER', url.toString(), self._continuity, self._action);
                    context.url = url.toString();
                    super.load(context, config, callbacks);
                }
            }

            hlsConfig.pLoader = CustomLoader as PlaylistLoaderConstructor;
        }
        this._hlsConfig = hlsConfig;
    }

    private _onPlayerPlay = async () => {
        this._shouldBePlaying = true;
        clearTimeout(this._resetStreamTimoutId);
        if (this.isDirectStream) {
            this._startNowPlayingPolling();
            return;
        }
        if (!this._shouldResetStreamOnPlay) {
            this._hls?.startLoad();
        } else {
            this.log('RESET STREAM');
            this._shouldResetStreamOnPlay = false;
            this._destroyPlayer();
            await this._initPlayer();
        }
    }

    private _onPlayerPause = () => {
        this._shouldBePlaying = false;
        if (this.isDirectStream) {
            this._stopNowPlayingPolling();
            return;
        }
        this._hls?.stopLoad();
        clearTimeout(this._resetStreamTimoutId);
        this._resetStreamTimoutId = setTimeout(() => {
            this._shouldResetStreamOnPlay = true;
        }, 15 * 60_000);
    }

    private _onAddTextTrack = (addTrackEvent: TrackEvent) => {
        if (!addTrackEvent.track) {
            return;
        }
        this.log('ADD TEXT TRACK', addTrackEvent.track.kind);
        if (this._isInteractive && this._useNativeHls && addTrackEvent.track.kind === 'subtitles') {
            addTrackEvent.track.mode = 'showing';
            addTrackEvent.track.addEventListener('cuechange', (cueChangeEvent: any) => {
                this.log('CUECHANGE (SUBTITLES TRACK)');
                let segmentData: Segment;
                for (const cue of cueChangeEvent.target?.activeCues || []) {
                    if (cue.track.kind == 'subtitles') {
                        switch (cue.id) {
                            case 'METADATA':
                                {
                                    const parsed = this._parseSegmentTitle(cue.text);
                                    if (parsed) {
                                        this._updateSegment(parsed);
                                    }
                                }
                                break;
                        }
                    }
                }
            });
        }

        if (addTrackEvent.track.kind === 'metadata') {
            // @ts-ignore
            addTrackEvent.track.default = true;
            addTrackEvent.track.mode = 'hidden';
            addTrackEvent.track.addEventListener('cuechange', (cueChangeEvent: any) => {
                this.log('CUECHANGE (METADATA TRACK)');
                const trackMetadata: Partial<TrackMetadata> = {
                    // duration_ms: this._latestTrackMetadata?.duration_ms,
                };

                for (const cue of cueChangeEvent.target?.activeCues || []) {
                    if (
                        cue.track.kind == 'metadata' &&
                        cue.value &&
                        cue.value.key !== undefined
                    ) {
                        switch (cue.value.key) {
                            case 'TXXX':
                                if (cue.value.info === 'TRACK_CID' || cue.value.info === 'TRACK_ID') {
                                    trackMetadata.id = cue.value.data;
                                }
                                   break;
                            case 'TIT2':
                                if (cue.value.data) {
                                    trackMetadata.title = cue.value.data;
                                }
                                break;
                            case 'TPE1':
                                if (cue.value.data) {
                                    trackMetadata.artist = cue.value.data;
                                }
                                break;
                            case 'TALB':
                                if (cue.value.data) {
                                    trackMetadata.album = cue.value.data;
                                }
                                break;
                            case 'WXXX':
                                if (cue.value.info === 'artworkURL_640x') {
                                    trackMetadata.image = cue.value.data;
                                }
                                break;
                            case 'TLEN':
                                if (!trackMetadata.duration_ms && cue.value.data) {
                                    const durationMs = parseInt(cue.value.data, 10);
                                    if (!isNaN(durationMs)) {
                                        trackMetadata.duration_ms = durationMs;
                                    }
                                }
                                break;
                        }
                    }
                }


                if (
                    trackMetadata.id &&
                    trackMetadata.title &&
                    trackMetadata.artist &&
                    // trackMetadata.album &&
                    // trackMetadata.image &&
                    trackMetadata.duration_ms
                ) {
                    this._updateTrackMetadata(trackMetadata as TrackMetadata, 'id3-cue');
                }
            });
        }
    }

    private log(...args: any[]) {
        if (!this._debugLogs) {
            return;
        }
        console.log('[shf log] >', ...args);
    }

    private logError(...args: any[]) {
        console.error('[shf log] >', ...args);
    }

    private async _initPlayer() {
        if (!this._mediaElem || !this._sourceUrl) {
            return;
        }
        this._playOnInit = this._shouldBePlaying || (!this._hasInitializedPlayer && this._autoPlay);
        this._hasInitializedPlayer = true;
        this._isLoading = true;
        this.emit(HlsPlusEvents.TRACK_LOADING);
        this._mediaElem.addEventListener('play', this._onPlayerPlay);
        this._mediaElem.addEventListener('pause', this._onPlayerPause);
        this._mediaElem.textTracks.addEventListener('addtrack', this._onAddTextTrack);

        if (this.isDirectStream) {
            await this._initDirectStream();
        } else if (this._useNativeHls) {
            this._mediaElem.removeAttribute('crossorigin');
            await this._initNativeHls();
            this._isLoading = false;
            this._updateAction('refresh');
            this.emit(HlsPlusEvents.TRACK_LOADED);
            if (this._playOnInit) {
                await this._mediaElem.play()
                    .catch((error) => {
                        this.logError('Unable to autoplay prior to user interaction with the dom.', error);
                    });
            }
        } else {
            this._mediaElem.removeAttribute('crossorigin');
            await this._unregisterWorker();
            // hls.js doesn't load if this is async! Needs investigation.
            // Would be cleaner to await until the load event
            this._initHlsJs();
        }
    }

    private _destroyPlayer() {
        if (this._mediaElem) {
            this._mediaElem.removeEventListener('play', this._onPlayerPlay);
            this._mediaElem.removeEventListener('pause', this._onPlayerPause);
            this._mediaElem.textTracks.removeEventListener('addtrack', this._onAddTextTrack);
            this._mediaElem.pause();
            this._mediaElem.src = '';
        }
        this._stopNowPlayingPolling();
        this._hls?.destroy();
    }

    private async _initDirectStream() {
        if (!this._mediaElem || !this._sourceUrl) {
            return;
        }
        await this._unregisterWorker();
        this._mediaElem.crossOrigin = 'anonymous';
        this._mediaElem.src = this._sourceUrl;
        this._isLoading = false;
        this._updateAction('refresh');
        this.emit(HlsPlusEvents.TRACK_LOADED);
        this._startNowPlayingPolling();
        if (this._playOnInit) {
            await this._mediaElem.play()
                .catch((error) => {
                    this.logError('Unable to autoplay prior to user interaction with the dom.', error);
                });
        }
    }

    private _initHlsJs() {
        if (!this._mediaElem || !this._sourceUrl) {
            return;
        }
        this._hls = new Hls(this._hlsConfig);
        this._hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
            this.log('MANIFEST_PARSED');
            this.emit(HlsPlusEvents.MANIFEST_PARSED, data);
            this._updateAction('refresh');
        });
        let firstLoad = true;
        this._hls.on(Hls.Events.FRAG_CHANGED, (event, data) => {
                if (data.frag.title) {
                try {
                    const newSegment = this._parseSegmentTitle(data.frag.title);
                    if (newSegment) {
                        this._updateSegment(newSegment);
                    }
                    if (firstLoad) {
                        firstLoad = false;
                        this._isLoading = false;
                        this.emit(HlsPlusEvents.TRACK_LOADED);
                        // Use the merged latest metadata (populated in _updateSegment)
                        // so duration_ms and other fields are normalized before
                        // emitting TRACK_METADATA_CHANGED.
                        if (this._latestTrackMetadata) {
                            this._updateTrackMetadata(this._latestTrackMetadata as TrackMetadata, 'frag-first-load');
                        } else if (newSegment) {
                            this._updateTrackMetadata(newSegment.current as TrackMetadata, 'frag-first-load');
                        }
                        if (this._playOnInit) {
                            this._mediaElem?.play()
                                .then(() => {
                                    this.log('PLAY')
                                })
                                .catch((error) => {
                                    this.logError('Unable to autoplay prior to user interaction with the dom.', error);
                                });
                        }
                    }
                } catch (error) {
                    if (firstLoad) {
                        firstLoad = false;
                        this._isLoading = false;
                        if (this._playOnInit) {
                            this._mediaElem?.play()
                                .then(() => {
                                    this.log('PLAY')
                                })
                                .catch((error) => {
                                    this.logError('Unable to autoplay prior to user interaction with the dom.', error);
                                });
                        }
                    }
                }
            }
        });

        this._hls.on(Hls.Events.ERROR, (event, data) => {
            this.logError('HLS Error', data, event);
            this._isLoading = false;
            if (data.fatal) {
                switch (data.type) {
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        this._hls?.recoverMediaError();
                        break;
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        break;
                    default:
                        this._destroyPlayer();
                        break;
                }
            }
        });

        this._hls.attachMedia(this._mediaElem);
        this._hls.loadSource(this._sourceUrl);
    }

    private _updateAction(action: typeof this._action) {
        this._action = action;
        this._syncWorker();
    }

    private _updateContinuity(continuity: typeof this._continuity) {
        this._continuity = continuity;
        this._syncWorker();
    }

    private _updateAvailableSkips(skips: number) {
        const prevSkips = this._availableSkips;
        this._availableSkips = this._isInteractive ? Math.max(skips, 0) : 0;
        if (this._availableSkips !== prevSkips) {
            this.emit(HlsPlusEvents.AVAILABLE_SKIPS_CHANGED, {
                availableSkips: this._availableSkips,
            });
        }
    }

    private _startNowPlayingPolling() {
        this._stopNowPlayingPolling();
        this._pollNowPlaying();
    }

    private _stopNowPlayingPolling() {
        if (this._nowPlayingPollTimeoutId) {
            clearTimeout(this._nowPlayingPollTimeoutId);
            this._nowPlayingPollTimeoutId = undefined;
        }
        this._nowPlayingAbortController?.abort();
        this._nowPlayingAbortController = undefined;
    }

    private _scheduleNowPlayingPoll(
        delayMs: number = this._nowPlayingPollIntervalMs,
        minDelayMs: number = NOW_PLAYING_MIN_POLL_INTERVAL_MS,
    ) {
        if (!this.isDirectStream) {
            return;
        }
        this._nowPlayingPollTimeoutId = setTimeout(() => {
            this._pollNowPlaying();
        }, Math.max(delayMs, minDelayMs));
    }

    private async _pollNowPlaying() {
        if (!this._sourceUrl || !isSuperhifiDirectStreamUrl(this._sourceUrl)) {
            return;
        }

        const nowPlayingConfig = buildNowPlayingConfig(
            this._sourceUrl,
            this._nowPlayingPartner,
            this._nowPlayingComposerId,
        );
        if (!nowPlayingConfig) {
            this.logError('Unable to derive nowPlaying route for direct stream');
            return;
        }

        const abortController = new AbortController();
        this._nowPlayingAbortController = abortController;
        try {
            const response = await fetch(nowPlayingConfig.url, {
                cache: 'no-store',
                credentials: 'omit',
                signal: abortController.signal,
            });
            if (!response.ok) {
                throw new Error(`nowPlaying returned ${response.status}`);
            }
            const data = await response.json();
            const tracks = extractNowPlayingTracks(data);
            const playingTrack = tracks.find((track) => track.status?.toLowerCase() === 'playing');
            if (!playingTrack) {
                throw new Error('nowPlaying did not include a playing track');
            }
            const nextDelayMs = this._updateNowPlayingTrack(playingTrack, tracks);
            if (nextDelayMs === null) {
                throw new Error('nowPlaying did not include next-track timing');
            }
            this._scheduleNowPlayingPoll(
                nextDelayMs,
                nextDelayMs < NOW_PLAYING_MIN_POLL_INTERVAL_MS ?
                    NOW_PLAYING_BOUNDARY_RETRY_MS :
                    NOW_PLAYING_MIN_POLL_INTERVAL_MS,
            );
        } catch (error) {
            if (!abortController.signal.aborted) {
                this.logError('nowPlaying unavailable', error);
                this._scheduleNowPlayingPoll(this._nowPlayingPollIntervalMs);
            }
        } finally {
            if (this._nowPlayingAbortController === abortController) {
                this._nowPlayingAbortController = undefined;
            }
        }
    }

    private _getNextQueuedTrackStartMs(tracks: NowPlayingTrack[], playingTrack: NowPlayingTrack): number | null {
        const nowMs = Date.now();
        const playingIndex = tracks.indexOf(playingTrack);
        if (playingIndex > 0) {
            const nextQueuedTrack = tracks
                .slice(0, playingIndex)
                .reverse()
                .find((track) => track.status?.toLowerCase() === 'queued' && !!track.time);
            const nextQueuedStartMs = nextQueuedTrack?.time ? Date.parse(nextQueuedTrack.time) : NaN;
            if (!isNaN(nextQueuedStartMs)) {
                return nextQueuedStartMs;
            }
        }

        const futureQueuedStartTimes = tracks
            .filter((track) => track.status?.toLowerCase() === 'queued' && !!track.time)
            .map((track) => Date.parse(track.time!))
            .filter((timeMs) => !isNaN(timeMs) && timeMs > nowMs)
            .sort((left, right) => left - right);
        return futureQueuedStartTimes[0] ?? null;
    }

    private _getNextTrackPollDelayMs(nowPlayingTrack: NowPlayingTrack, tracks: NowPlayingTrack[]): number | null {
        const nextQueuedStartMs = this._getNextQueuedTrackStartMs(tracks, nowPlayingTrack);
        const durationMs = nowPlayingTrack.duration_ms || parseDurationMs(nowPlayingTrack.duration);
        const startTimeMs = nowPlayingTrack.time ? Date.parse(nowPlayingTrack.time) : NaN;
        const fallbackNextStartMs = !isNaN(startTimeMs) && durationMs ? startTimeMs + durationMs : null;
        const nextStartMs = nextQueuedStartMs || fallbackNextStartMs;
        if (!nextStartMs) {
            return null;
        }

        const delayMs = nextStartMs - Date.now() + NOW_PLAYING_BOUNDARY_OFFSET_MS;
        if (delayMs <= 0) {
            return NOW_PLAYING_BOUNDARY_RETRY_MS;
        }
        return delayMs;
    }

    private _updateNowPlayingTrack(nowPlayingTrack: NowPlayingTrack, tracks: NowPlayingTrack[] = []): number | null {
        const durationMs = nowPlayingTrack.duration_ms || parseDurationMs(nowPlayingTrack.duration);
        const startTimeMs = nowPlayingTrack.time ? Date.parse(nowPlayingTrack.time) : NaN;
        const elapsedMs = !isNaN(startTimeMs) ? Math.max(Date.now() - startTimeMs, 0) : 0;
        const id = nowPlayingTrack.id || nowPlayingTrack.cartid || `${nowPlayingTrack.artist || ''}-${nowPlayingTrack.title || ''}-${nowPlayingTrack.time || ''}`;
        const current: Track = {
            id,
            title: nowPlayingTrack.title || '',
            album: nowPlayingTrack.album || '',
            artist: nowPlayingTrack.artist || '',
            duration_ms: durationMs,
            duration: durationMs / 1000,
            start_ms: nowPlayingTrack.time || '',
            image: firstString(nowPlayingTrack.imageUrl) || nowPlayingTrack.image || '',
            type: nowPlayingTrack.type || '',
            companion: null,
            elapsed_ms: durationMs ? Math.min(elapsedMs, durationMs) : elapsedMs,
        };
        this._updateSegment({
            current,
            available_skips: 0,
            skip_return_ms: 0,
            continuity: 0,
        });
        return this._getNextTrackPollDelayMs(nowPlayingTrack, tracks);
    }

    private async _initNativeHls() {
        await this._registerWorker('hls-service-worker.js');
        return new Promise<void>((resolve, reject) => {
            if (!this._mediaElem || !this._sourceUrl) {
                return;
            }
            const onLoadedData = () => {
                this._mediaElem?.removeEventListener('loadeddata', onLoadedData);
                this._mediaElem?.play()
                    .then(() => {
                        this._mediaElem?.pause();
                        resolve();
                    })
                    .catch(error => {
                        this.logError(error);
                        resolve();
                    });
            };
            this._mediaElem.addEventListener('loadeddata', onLoadedData);
            this._mediaElem.src = this._sourceUrl;
        })
    }

    private async _registerWorker(scriptUrl: string) {
        if (!navigator['serviceWorker']) {
            return;
        }
        if (!this._workerReg || !this._workerReg.active) {
            const reg = await navigator.serviceWorker.register(scriptUrl);
            this._workerReg = reg;
            if (!reg.active) {
                await navigator.serviceWorker.ready;
            }
        }
        this._syncWorker();
    }

    private async _unregisterWorker() {
        if (!navigator['serviceWorker']) {
            return;
        }
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (!registrations.length) {
            return;
        }
        for (const reg of registrations) {
            await reg.unregister();
        }
    }

    private _syncWorker() {
        if (!navigator['serviceWorker']) {
            return;
        }
        this._workerReg?.active?.postMessage(JSON.stringify({
            action: this._action,
            continuity: this._continuity,
            forwardedFor: this._forwardedFor,
            adUrl: this._adUrl,
        }));
    }

    private _parseSegmentTitle(titleStr: string): Segment | null {
        if (!titleStr) return null;

        // Try JSON first (existing format)
        try {
            const parsed = JSON.parse(titleStr);
            // Basic validation
            if (parsed && parsed.current) {
                const timeleftMs = parsed.current.timeleft_ms || parsed.current.milliseconds_left;
                if (timeleftMs && !parsed.current.elapsed_ms && parsed.current.duration_ms) {
                    // Compute elapsed_ms from timeleft_ms and duration_ms when possible
                    parsed.current.elapsed_ms = Math.round(parsed.current.duration_ms - timeleftMs);
                }
                return parsed as Segment;
            }
        } catch (e) {
            // continue to XML parsing
        }

        // Try XML (some streams include XML in EXTINF)
        try {
            if (typeof DOMParser === 'undefined') return null;
            const parser = new DOMParser();
            const doc = parser.parseFromString(titleStr, 'text/xml');
            const now = doc.getElementsByTagName('nowplaying')[0] || doc.getElementsByTagName('nowPlaying')[0];
            if (!now) return null;

            const getText = (tag: string) => {
                const el = now.getElementsByTagName(tag)[0];
                return el && el.textContent ? el.textContent.trim() : '';
            };

            const durationVal = parseFloat(getText('duration') || getText('duration_ms') || '0') || 0; // duration in ms
            const millisecondsLeft = parseFloat(getText('milliseconds_left') || '0') || 0; // remaining ms, if provided

            // Compute elapsed_ms when possible. Some streams provide
            // `milliseconds_left` (remaining time). Convert that to elapsed
            // by subtracting from duration (both values in ms).
            let elapsedMs: number | undefined = undefined;
            if (durationVal && millisecondsLeft) {
                const calc = Math.round(durationVal - millisecondsLeft);
                // clamp to valid range
                elapsedMs = Math.max(0, Math.min(calc, Math.round(durationVal)));
            }

            const current: Track = {
                id: getText('id') || getText('track_id') || '',
                title: getText('title') || '',
                album: getText('album') || '',
                artist: getText('artist') || '',
                duration_ms: Math.round(durationVal),
                duration: durationVal ? durationVal / 1000 : 0,
                start_ms: getText('air_time') || getText('start') || getText('start_ms') || '',
                image: '',
                type: getText('media_type') || '',
                companion: null,
                elapsed_ms: elapsedMs,
            };
            

            const seg: Segment = {
                current,
                available_skips: 0,
                skip_return_ms: 3600000, // default to 1 hour if not provided
                continuity: 0,
            };

            return seg;
        } catch (e) {
            this.logError('Failed to parse EXTINF metadata as XML', e);
            return null;
        }
    }

    private _updateSegment(segment: Segment) {
        this._latestSegment = segment;


        // Merge values from the new segment's current track into the cached
        // latest track metadata so consumers don't read stale fields while
        // ID3 cues are still arriving. Also emit TRACK_CHANGED /
        // TRACK_METADATA_CHANGED based on the merged result so UI listeners
        // update immediately.
        try {
            const current = segment.current || ({} as Track);
            let adAlbum = '';
           if (current.companion?.image) {
                adAlbum = 'Companion Ad';
            } else if (current.type && ['ad', 'advertisement'].includes(current.type.toLowerCase())) {
                adAlbum = current.type;
            } 

            if (adAlbum && current.album && adAlbum.toLowerCase() === current.album.toLowerCase()) {
                // Prevent ad album from overwriting real album on consecutive ads
                adAlbum = '';
            }

            const prevId = this._latestTrackMetadata?.id || '';
            const nextId = current.id || prevId;
            const sameTrack = !!nextId && nextId === prevId;

            const merged: Partial<TrackMetadata> = {
                ...(this._latestTrackMetadata || {}),
                id: current.id || this._latestTrackMetadata?.id,
                title: current.title || this._latestTrackMetadata?.title,
                artist: current.artist || '',
                album: current.album || adAlbum || this._latestTrackMetadata?.album || '',
                image: current.image || current.companion?.image || (sameTrack ? this._latestTrackMetadata?.image : '') || '',
                duration: current.duration || this._latestTrackMetadata?.duration,
                duration_ms: current.duration_ms ?? (
                    current.duration ? Math.round(current.duration * 1000) : this._latestTrackMetadata?.duration_ms
                ),
            };

            // Let _updateTrackMetadata handle assigning and emitting changes
            this._updateTrackMetadata(merged as TrackMetadata, 'segment');
        } catch (e) {
            this.logError('Error merging segment into track metadata', e);
        }

        this.log('SEGMENT_CHANGED', segment);
        this.emit(HlsPlusEvents.SEGMENT_CHANGED, this._latestSegment);
        this._updateContinuity(segment.continuity);
        this._updateAvailableSkips(this._latestSegment.available_skips);
    }

    private _updateTrackMetadata(trackMetadata: TrackMetadata, caller?: string) {
        // Debounce/coalesce nearly-simultaneous metadata updates so the UI
        // doesn't flicker when ID3 cues and fragment metadata arrive together.
        this._pendingTrackMetadata = trackMetadata;
        if (this._trackMetadataDebounceTimer) {
            clearTimeout(this._trackMetadataDebounceTimer);
        }
        this._trackMetadataDebounceTimer = setTimeout(() => {
            const incoming = this._pendingTrackMetadata!;
            const prev = this._latestTrackMetadata || {};
            const prevExists = !!this._latestTrackMetadata;

            const prevId = prev.id || '';
            const prevTitle = prev.title || '';
            const prevArtist = prev.artist || '';
            const prevAlbum = prev.album || '';
            const prevDurationMs = prev.duration_ms ?? 0;
            const prevImage = prev.image || '';

            const incomingId = incoming.id || '';
            const sameTrack = !!incomingId && incomingId === prevId;
            

            // Normalize optional fields so that missing values (e.g., in ads)
            // don't count as a change when a previous value exists.
            const normalized: TrackMetadata = {
                id: incoming.id || '',
                title: incoming.title || '',
                artist: incoming.artist || '',
                album: incoming.album || '',
                image: incoming.image || (sameTrack ? prev.image : '') || '',
                duration_ms: incoming.duration_ms ?? 0,
                duration: incoming.duration ?? 0,
            };

            if (normalized.duration && !normalized.duration_ms) {
                normalized.duration_ms = Math.round(normalized.duration * 1000);
            }

            if (prev.duration && !prev.duration_ms) {
                prev.duration_ms = Math.round(prev.duration * 1000);
            }

            const stripImageQuery = (url: string) => {
                if (!url) return '';
                const qIndex = url.indexOf('?');
                const hIndex = url.indexOf('#');
                if (qIndex === -1 && hIndex === -1) return url;
                if (qIndex === -1) return url.slice(0, hIndex);
                if (hIndex === -1) return url.slice(0, qIndex);
                return url.slice(0, Math.min(qIndex, hIndex));
            };

            const normalizedImageForDiff = stripImageQuery(normalized.image);
            const prevImageForDiff = stripImageQuery(prevImage);

            this._latestTrackMetadata = normalized;

            if (normalized.id !== prevId) {
                this.log('TRACK_CHANGED', {caller, inSong: normalized, outSong: prev}); 
                this.emit(HlsPlusEvents.TRACK_CHANGED, normalized);
            } else if (prevExists && (
                normalized.id !== prevId ||
                normalized.title !== prevTitle ||
                normalized.artist !== prevArtist ||
                normalized.album !== prevAlbum ||
                normalizedImageForDiff !== prevImageForDiff ||
                normalized.duration_ms !== prevDurationMs
            )) {
                this.log('TRACK_METADATA_CHANGED', {caller, inSong: this._latestTrackMetadata, outSong: prev});
                this.emit(HlsPlusEvents.TRACK_METADATA_CHANGED, this._latestTrackMetadata);
            }

            this._pendingTrackMetadata = undefined;
            this._trackMetadataDebounceTimer = undefined;
        }, 20);
    }

    get isInteractive() {
        return this._isInteractive && !this.isDirectStream;
    }

    get isDirectStream() {
        return !!this._sourceUrl && isSuperhifiDirectStreamUrl(this._sourceUrl);
    }

    static isSupported(streamUrl?: string) {
        if (!streamUrl || isM3u8Url(streamUrl)) {
            return Hls.isSupported();
        }
        const mediaElem = document.createElement('audio');
        return isSuperhifiDirectStreamUrl(streamUrl) && !!(
            mediaElem.canPlayType('audio/aac') ||
            mediaElem.canPlayType('audio/aacp') ||
            mediaElem.canPlayType('audio/mpeg')
        );
    }

    async loadSource(url: string) {
        this._destroyPlayer();
        this._sourceUrl = url;
        await this._initPlayer();
    }

    async attachMedia(media: HTMLMediaElement) {
        this._destroyPlayer();
        this._mediaElem = media;
        await this._initPlayer();
    }

    async refresh(options: {reset?: boolean; resume?: boolean} = {}) {
        this._updateAction('refresh');
        if (options.resume) {
            this._shouldBePlaying = true;
        }
        if (this.isDirectStream) {
            if (options.resume && this._mediaElem?.paused) {
                await this._mediaElem.play()
                    .catch((error) => {
                        this.logError('Unable to autoplay prior to user interaction with the dom.', error);
                    });
            } else if (!this._mediaElem?.paused) {
                this._startNowPlayingPolling();
            }
            return;
        }

        if (options.reset) {
            const wasPlaying = !!this._mediaElem && !this._mediaElem.paused;
            this._shouldBePlaying = !!(options.resume || wasPlaying);
            this._destroyPlayer();
            await this._initPlayer();
            return;
        }

        if (this._useNativeHls) {
            this._syncWorker();
            return;
        }

        if (this._hls && !this._mediaElem?.paused) {
            this._hls.stopLoad();
            this._hls.startLoad(-1);
        }
    }

    get canSkip(): boolean {
        return !!(
            !this._isSkipping &&
            !this._isLoading &&
            this._isInteractive &&
            this._latestSegment &&
            this._latestSegment.available_skips > 0
        );
    }

    async skip() {
        if (!this.canSkip) {
            return;
        }
        this.log('SKIP');
        this._isSkipping = true;
        this._destroyPlayer();
        this._updateAction('skip');
        this._shouldBePlaying = true;
        await this._initPlayer();
        this._isSkipping = false;
    }
}
